import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  useTheme,
  TableSortLabel,
  Collapse,
  Tooltip,
} from '@mui/material';
import { FilterContext } from '../../contexts/FilterContext';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';
import AdStatisticsDetailModal from './AdStatisticsDetailModal';
import AdPreviewPopover from './AdPreviewPopover';

interface AdStatistic {
  ad_id: number;
  ad_control_code: number;
  subject: string;
  from_name: string;
  preview_text: string;
  form: string;
  product_customer: string;
  product_name: string;
  esp_type: string;
  send_count: number;
  first_send_date: string;
  last_send_date: string;
  total_recipients: number;
  total_opens: number;
  total_clicks: number;
  total_leads: number;
  total_revenue: number;
  open_rate: number;
  click_rate: number;
  lead_rate: number;
  etko: number;
  avg_recipients: number;
  avg_opens: number;
  avg_clicks: number;
  avg_leads: number;
  avg_revenue: number;
  anfo_count: number;
  anfo_30d_revenue: number;
  roi_30: number;
}

interface Filters {
  customers: string[];
  forms: string[];
  newsletters: string[];
}

interface ApiSummary {
  total_recipients: number;
  total_opens: number;
  total_opens_unique: number; // Deduplicated opens for accurate ETKO calculation
  total_clicks: number;
  total_leads: number;
  total_revenue: number;
}

type OrderBy = keyof AdStatistic;
type Order = 'asc' | 'desc';

const AdStatisticsTable: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;

  const [data, setData] = useState<AdStatistic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [selectedAd, setSelectedAd] = useState<AdStatistic | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [apiSummary, setApiSummary] = useState<ApiSummary | null>(null);

  // Hover preview state
  const [hoveredAdId, setHoveredAdId] = useState<number | null>(null);
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | undefined>(
    undefined,
  );
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Filters
  const [filters, setFilters] = useState<Filters>({
    customers: [],
    forms: [],
    newsletters: [],
  });
  const [showLegend, setShowLegend] = useState(true);
  const [selectedNewsletters, setSelectedNewsletters] = useState<string[]>([]);
  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [selectedEsp, setSelectedEsp] = useState<string>('all');
  const [minRecipients, setMinRecipients] = useState<number>(10);

  // Sorting
  const [orderBy, setOrderBy] = useState<OrderBy>('lead_rate');
  const [order, setOrder] = useState<Order>('desc');

  // Load available filters
  useEffect(() => {
    const loadFilters = async () => {
      if (!filterState?.startDate || !filterState?.endDate) {
        return;
      }

      try {
        // Format dates to YYYY-MM-DD
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const options = {
          start_date: formatDate(filterState.startDate!),
          end_date: formatDate(filterState.endDate!),
        };

        const response = (await fetchData('/api/ad-statistics/filters', options, false)) as any;
        const newFilters = {
          customers: response.customers || [],
          forms: response.forms || [],
          newsletters: response.newsletters || [],
        };

        setFilters(newFilters);
      } catch (err) {
        console.error('Error loading filters:', err);
      }
    };
    loadFilters();
  }, [filterState?.startDate, filterState?.endDate]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!filterState?.startDate || !filterState?.endDate) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Format dates to YYYY-MM-DD
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const options: any = {
          start_date: formatDate(filterState.startDate!),
          end_date: formatDate(filterState.endDate!),
          esp: selectedEsp,
          newsletter: selectedNewsletters.length > 0 ? selectedNewsletters : undefined,
          form: selectedForms.length > 0 ? selectedForms : undefined,
          min_recipients: minRecipients,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          customer_id:
            filterState.customers && filterState.customers.length > 0
              ? filterState.customers
              : undefined,
        };

        const response = (await fetchData('/api/ad-statistics', options, false)) as any;

        if (response.data) {
          setData(response.data);
          setTotal(response.pagination?.total || 0);
          setApiSummary(response.summary || null);
        }
      } catch (err: any) {
        console.error('Error loading ad statistics:', err);
        setError(err.message || 'Failed to load ad statistics');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    filterState,
    selectedNewsletters,
    selectedForms,
    selectedEsp,
    minRecipients,
    page,
    rowsPerPage,
  ]);

  // Cleanup hover timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  const handleEspChange = (event: SelectChangeEvent) => {
    setSelectedEsp(event.target.value);
    setPage(0);
  };

  const handleNewsletterChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedNewsletters(typeof value === 'string' ? [value] : value);
    setPage(0);
  };

  const handleFormChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedForms(typeof value === 'string' ? [value] : value);
    setPage(0);
  };

  const handleRowClick = (ad: AdStatistic) => {
    // Clear any pending hover preview when clicking
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    setHoveredAdId(null);
    setAnchorPosition(undefined);

    setSelectedAd(ad);
    setModalOpen(true);
  };

  const handleMouseEnter = (ad: AdStatistic, event: React.MouseEvent) => {
    // Clear any existing timer
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    // Set a new timer for 300ms delay
    hoverTimerRef.current = setTimeout(() => {
      setHoveredAdId(ad.ad_id);
      setAnchorPosition({
        top: event.clientY + 10, // Offset to avoid cursor overlap
        left: event.clientX + 10,
      });
    }, 300);
  };

  const handleMouseLeave = () => {
    // Clear the timer to prevent showing preview
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    // Hide the preview
    setHoveredAdId(null);
    setAnchorPosition(undefined);
  };

  const handlePreviewClose = () => {
    setHoveredAdId(null);
    setAnchorPosition(undefined);
  };

  const handleSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle currency fields (ETKO, Revenue) - parse numbers for proper sorting
      if (orderBy === 'etko' || orderBy === 'total_revenue') {
        aValue = typeof aValue === 'string' ? parseFloat(aValue) : aValue || 0;
        bValue = typeof bValue === 'string' ? parseFloat(bValue) : bValue || 0;
        aValue = isNaN(aValue) ? 0 : aValue;
        bValue = isNaN(bValue) ? 0 : bValue;
      }

      if (aValue === null || aValue === undefined) return order === 'asc' ? 1 : -1;
      if (bValue === null || bValue === undefined) return order === 'asc' ? -1 : 1;

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [data, order, orderBy]);

  const summaryData = React.useMemo(() => {
    if (data.length === 0) return null;

    const totalSends = data.reduce((sum, ad) => sum + (ad.send_count || 0), 0);
    const totalRecipients = data.reduce((sum, ad) => sum + (ad.total_recipients || 0), 0);
    const totalOpens = data.reduce((sum, ad) => sum + (ad.total_opens || 0), 0);
    const totalClicks = data.reduce((sum, ad) => sum + (ad.total_clicks || 0), 0);
    const totalLeads = data.reduce((sum, ad) => sum + (ad.total_leads || 0), 0);
    const totalRevenue = data.reduce((sum, ad) => {
      const revenue =
        typeof ad.total_revenue === 'string' ? parseFloat(ad.total_revenue) : ad.total_revenue || 0;
      return sum + (isNaN(revenue) ? 0 : revenue);
    }, 0);

    // Use unique opens from API summary for accurate ETKO (campaign opens are duplicated per ad)
    const totalOpensUnique = apiSummary?.total_opens_unique || totalOpens;

    // Calculate weighted averages for rates
    const avgOpenRate = totalRecipients > 0 ? (totalOpens / totalRecipients) * 100 : 0;
    const avgClickRate = totalOpens > 0 ? (totalClicks / totalOpens) * 100 : 0;
    const avgLeadRate = totalOpens > 0 ? (totalLeads / totalOpens) * 100 : 0;
    // Use unique opens for ETKO to avoid overcounting
    const avgEtko = totalOpensUnique > 0 ? (totalRevenue / totalOpensUnique) * 1000 : 0;

    // ANFO metrics
    const totalAnfoCount = data.reduce((sum, ad) => sum + (ad.anfo_count || 0), 0);
    const totalAnfo30dRevenue = data.reduce((sum, ad) => {
      const revenue = ad.anfo_30d_revenue || 0;
      const numRevenue = typeof revenue === 'string' ? parseFloat(revenue) : revenue;
      return sum + (isNaN(numRevenue) ? 0 : numRevenue);
    }, 0);

    // Calculate average ROI (exclude null values)
    const validRoi30Values = data
      .map((ad) => {
        if (ad.roi_30 == null) return null;
        const numRoi = typeof ad.roi_30 === 'string' ? parseFloat(ad.roi_30) : ad.roi_30;
        return isNaN(numRoi) ? null : numRoi;
      })
      .filter((val): val is number => val !== null);
    const avgRoi30 =
      validRoi30Values.length > 0
        ? validRoi30Values.reduce((sum, val) => sum + val, 0) / validRoi30Values.length
        : 0;

    return {
      ad_count: data.length,
      send_count: totalSends,
      total_recipients: totalRecipients,
      total_opens: totalOpens,
      total_clicks: totalClicks,
      total_leads: totalLeads,
      total_revenue: totalRevenue,
      open_rate: avgOpenRate,
      click_rate: avgClickRate,
      lead_rate: avgLeadRate,
      etko: avgEtko,
      anfo_count: totalAnfoCount,
      anfo_30d_revenue: totalAnfo30dRevenue,
      roi_30: avgRoi30,
    };
  }, [data, apiSummary]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('de-DE');
  };

  const formatRate = (rate: number | null | undefined) => {
    if (rate === null || rate === undefined) return '-';
    const numRate = typeof rate === 'string' ? parseFloat(rate) : rate;
    if (isNaN(numRate)) return '-';
    return `${numRate.toFixed(2)}%`;
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return '-';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '-';
    return `€${numAmount.toFixed(2)}`;
  };

  if (loading && data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>ESP</InputLabel>
          <Select value={selectedEsp} onChange={handleEspChange} label="ESP">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="mailchimp">Mailchimp</MenuItem>
            <MenuItem value="iterable">Iterable</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Newsletter</InputLabel>
          <Select
            multiple
            value={selectedNewsletters}
            onChange={handleNewsletterChange}
            input={<OutlinedInput label="Newsletter" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {filters.newsletters.map((newsletter) => (
              <MenuItem key={newsletter} value={newsletter}>
                {newsletter}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Form</InputLabel>
          <Select
            multiple
            value={selectedForms}
            onChange={handleFormChange}
            input={<OutlinedInput label="Form" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {filters.forms.map((form) => (
              <MenuItem key={form} value={form}>
                {form}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Min. Empfänger</InputLabel>
          <Select
            value={minRecipients}
            onChange={(e) => {
              setMinRecipients(Number(e.target.value));
              setPage(0);
            }}
            label="Min. Empfänger"
          >
            <MenuItem value={1}>Alle</MenuItem>
            <MenuItem value={10}>≥ 10</MenuItem>
            <MenuItem value={100}>≥ 100</MenuItem>
            <MenuItem value={1000}>≥ 1000</MenuItem>
            <MenuItem value={10000}>≥ 10000</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Data Scope Legend */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="h6" fontSize="1rem" fontWeight="bold">
            ℹ️ Wichtige Hinweise zur Datenauswertung
          </Typography>
          <Typography
            variant="body2"
            sx={{
              cursor: 'pointer',
              color: colors.grey[100],
              '&:hover': { color: colors.primary[100] },
            }}
            onClick={() => setShowLegend(!showLegend)}
          >
            {showLegend ? 'Ausblenden' : 'Einblenden'}
          </Typography>
        </Box>
        <Collapse in={showLegend}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: colors.primary[400],
              border: `1px solid ${colors.grey[700]}`,
            }}
          >
            <Typography variant="body2" gutterBottom>
              <strong>Diese Auswertung zeigt aggregierte Kampagnen-Metriken</strong> aus der Tabelle
              mc_nl_ads_statistics.
            </Typography>

            <Typography variant="body2" component="div" sx={{ mt: 2 }}>
              <strong>Metriken in der Übersichtstabelle:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem', paddingLeft: '1.5rem' }}>
                <li>
                  <strong>Empfänger & Öffnungen:</strong> Zeigen die{' '}
                  <strong>gesamte Kampagnenzahl</strong> (alle Kontrollcodes zusammen)
                </li>
                <li>
                  <strong>Klicks, Leads & Umsatz:</strong> Aggregiert über alle verwendeten
                  Kontrollcodes
                </li>
                <li>Raten werden auf Basis dieser aggregierten Metriken berechnet</li>
              </ul>
            </Typography>

            <Typography variant="body2" component="div" sx={{ mt: 2 }}>
              <strong>Detaillierte Control Code Auswertung (ab 2025-12-01):</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem', paddingLeft: '1.5rem' }}>
                <li>
                  <strong>Klicken Sie auf eine Anzeige</strong> um die Detail-Ansicht zu öffnen
                </li>
                <li>
                  Für Anzeigen ab dem <strong>01.12.2025</strong> sehen Sie eine Aufschlüsselung
                  nach Control Code
                </li>
                <li>
                  Jeder Control Code zeigt separate Metriken: Empfänger, Öffnungen, Klicks, Leads,
                  Umsatz
                </li>
                <li>Datengrundlage: Tägliche Snapshots der Nutzerzuordnungen zu Control Codes</li>
              </ul>
            </Typography>

            <Typography variant="body2" component="div" sx={{ mt: 2 }}>
              <strong>Was sind Control Codes?</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem', paddingLeft: '1.5rem' }}>
                <li>
                  Control Codes steuern, welche Anzeigen Nutzer basierend auf ihren
                  Kundenabonnements sehen
                </li>
                <li>
                  <strong>Code 0:</strong> Nutzer ohne Kundenabonnements (~92% der aktiven Nutzer)
                </li>
                <li>
                  <strong>Codes 2,4,6,7 etc.:</strong> Nutzer mit spezifischen
                  Kundenabonnement-Kombinationen
                </li>
                <li>
                  Sowohl Mailchimp als auch Iterable verwenden dieselbe Campaign-ID, aber
                  personalisierte Anzeigen via Template-Logik
                </li>
              </ul>
            </Typography>

            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 2, display: 'block', fontStyle: 'italic' }}
            >
              Hinweis: Anzeigen vor dem 01.12.2025 haben keine detaillierte Control Code
              Aufschlüsselung verfügbar.
            </Typography>
          </Paper>
        </Collapse>
      </Box>

      {/* Summary Metrics */}
      {summaryData && data.length > 0 && (
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: colors.primary[400],
              border: `1px solid ${colors.grey[700]}`,
            }}
          >
            <Typography variant="caption" color="textSecondary">
              Anzahl Anzeigen
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {formatNumber(summaryData.ad_count)}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: colors.primary[400],
              border: `1px solid ${colors.grey[700]}`,
            }}
          >
            <Typography variant="caption" color="textSecondary">
              Anzahl Versendungen
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {formatNumber(summaryData.send_count)}
            </Typography>
          </Paper>

          <Tooltip title="Gesamte Empfänger über alle Anzeigen und Versendungen">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Σ Empfänger
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(summaryData.total_recipients)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Gesamte Öffnungen über alle Anzeigen und Versendungen">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Σ Öffnungen
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(summaryData.total_opens)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Gesamte Klicks über alle Anzeigen und Versendungen">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Σ Clicks
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(summaryData.total_clicks)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Gesamte Leads über alle Anzeigen und Versendungen">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Σ Leads
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(summaryData.total_leads)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Gewichtete durchschnittliche Öffnungsrate über alle Anzeigen (Gesamt-Öffnungen / Gesamt-Empfänger)">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø Öffnungsrate
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatRate(summaryData.open_rate)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Gewichtete durchschnittliche Klickrate über alle Anzeigen (Gesamt-Klicks / Gesamt-Öffnungen)">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø Klickrate
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatRate(summaryData.click_rate)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Gesamtumsatz über alle Anzeigen und Versendungen">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Σ Umsatz
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(summaryData.total_revenue)}
              </Typography>
            </Paper>
          </Tooltip>

          <Tooltip title="Umsatzeffizienz pro tausend Öffnungen über alle Anzeigen">
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                cursor: 'help',
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Ø ETKO
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(summaryData.etko)}
              </Typography>
            </Paper>
          </Tooltip>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'ad_id'}
                  direction={orderBy === 'ad_id' ? order : 'asc'}
                  onClick={() => handleSort('ad_id')}
                >
                  Anzeigen-ID
                </TableSortLabel>
              </TableCell>
              <TableCell>Kunde</TableCell>
              <TableCell>Produkt</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'form'}
                  direction={orderBy === 'form' ? order : 'asc'}
                  onClick={() => handleSort('form')}
                >
                  Formular
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'send_count'}
                  direction={orderBy === 'send_count' ? order : 'asc'}
                  onClick={() => handleSort('send_count')}
                >
                  Versendungen
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <Tooltip title="Zeitraum vom ersten bis zum letzten Versand dieser Anzeige">
                  <span>Versandzeitraum</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'total_recipients'}
                  direction={orderBy === 'total_recipients' ? order : 'asc'}
                  onClick={() => handleSort('total_recipients')}
                >
                  Empfänger
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'total_opens'}
                  direction={orderBy === 'total_opens' ? order : 'asc'}
                  onClick={() => handleSort('total_opens')}
                >
                  Öffnungen
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'total_clicks'}
                  direction={orderBy === 'total_clicks' ? order : 'asc'}
                  onClick={() => handleSort('total_clicks')}
                >
                  Klicks
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'total_leads'}
                  direction={orderBy === 'total_leads' ? order : 'asc'}
                  onClick={() => handleSort('total_leads')}
                >
                  Leads
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Öffnungen ÷ Empfänger × 100. Gewichteter Durchschnitt über alle Versendungen.">
                  <TableSortLabel
                    active={orderBy === 'open_rate'}
                    direction={orderBy === 'open_rate' ? order : 'asc'}
                    onClick={() => handleSort('open_rate')}
                  >
                    Öffnungsrate
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Klicks ÷ Öffnungen × 100. Gewichteter Durchschnitt über alle Versendungen.">
                  <TableSortLabel
                    active={orderBy === 'click_rate'}
                    direction={orderBy === 'click_rate' ? order : 'asc'}
                    onClick={() => handleSort('click_rate')}
                  >
                    Klickrate
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Leads ÷ Öffnungen × 100. Gewichteter Durchschnitt über alle Versendungen.">
                  <TableSortLabel
                    active={orderBy === 'lead_rate'}
                    direction={orderBy === 'lead_rate' ? order : 'asc'}
                    onClick={() => handleSort('lead_rate')}
                  >
                    Lead-Rate
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'total_revenue'}
                  direction={orderBy === 'total_revenue' ? order : 'asc'}
                  onClick={() => handleSort('total_revenue')}
                >
                  Umsatz
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Umsatz ÷ Öffnungen × 1000. Ertrag pro Tausend Öffnungen.">
                  <TableSortLabel
                    active={orderBy === 'etko'}
                    direction={orderBy === 'etko' ? order : 'asc'}
                    onClick={() => handleSort('etko')}
                  >
                    ETKO
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'anfo_count'}
                  direction={orderBy === 'anfo_count' ? order : 'asc'}
                  onClick={() => handleSort('anfo_count')}
                >
                  ANFO Anzahl
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'anfo_30d_revenue'}
                  direction={orderBy === 'anfo_30d_revenue' ? order : 'asc'}
                  onClick={() => handleSort('anfo_30d_revenue')}
                >
                  Anfo-Umsatz 30d €
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'roi_30'}
                  direction={orderBy === 'roi_30' ? order : 'asc'}
                  onClick={() => handleSort('roi_30')}
                >
                  ROI 30 %
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summaryData && (
              <TableRow
                sx={{
                  backgroundColor:
                    theme.palette.mode === 'dark' ? colors.primary[700] : colors.grey[300],
                  fontWeight: 'bold',
                  '& td': {
                    fontWeight: 'bold !important',
                    borderBottom: '2px solid',
                    borderColor:
                      theme.palette.mode === 'dark' ? colors.primary[500] : colors.grey[500],
                    color: '#ffffff !important',
                  },
                }}
              >
                <TableCell>-</TableCell>
                <TableCell>
                  <Tooltip title="Nur Anzeigen mit Kontrollcode 0 (Nutzer ohne Kundenabonnements)">
                    <span>Insgesamt (nur Code 0)</span>
                  </Tooltip>
                </TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell align="right">Σ {formatNumber(summaryData.send_count)}</TableCell>
                <TableCell>-</TableCell>
                <TableCell align="right">
                  <Tooltip title="Kampagnensumme (alle Codes)">
                    <span>Σ {formatNumber(summaryData.total_recipients)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Kampagnensumme (alle Codes)">
                    <span>Σ {formatNumber(summaryData.total_opens)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Nur Code 0">
                    <span>Σ {formatNumber(summaryData.total_clicks)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Nur Code 0">
                    <span>Σ {formatNumber(summaryData.total_leads)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Ø {formatRate(summaryData.open_rate)}</TableCell>
                <TableCell align="right">Ø {formatRate(summaryData.click_rate)}</TableCell>
                <TableCell align="right">Ø {formatRate(summaryData.lead_rate)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Nur Code 0">
                    <span>Σ {formatCurrency(summaryData.total_revenue || 0)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Ø {formatCurrency(summaryData.etko || 0)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Nur Code 0">
                    <span>Σ {formatNumber(summaryData.anfo_count)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Nur Code 0">
                    <span>Σ {formatCurrency(summaryData.anfo_30d_revenue)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Ø {formatNumber(summaryData.roi_30)}%</TableCell>
              </TableRow>
            )}
            {sortedData.map((ad) => (
              <TableRow
                key={ad.ad_id}
                hover
                onClick={() => handleRowClick(ad)}
                onMouseEnter={(event) => handleMouseEnter(ad, event)}
                onMouseLeave={handleMouseLeave}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{ad.ad_id}</TableCell>
                <TableCell>{ad.product_customer || '-'}</TableCell>
                <TableCell>{ad.product_name || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={ad.form || '-'}
                    size="small"
                    color={
                      ad.form === 'html'
                        ? 'error'
                        : ad.form === 'native'
                          ? 'success'
                          : ad.form === 'checkpoint'
                            ? 'warning'
                            : 'default'
                    }
                  />
                </TableCell>
                <TableCell align="right">{formatNumber(ad.send_count)}</TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {formatDate(ad.first_send_date)}
                    {ad.first_send_date !== ad.last_send_date && (
                      <>
                        <br />- {formatDate(ad.last_send_date)}
                      </>
                    )}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(ad.total_recipients)}</TableCell>
                <TableCell align="right">{formatNumber(ad.total_opens)}</TableCell>
                <TableCell align="right">{formatNumber(ad.total_clicks)}</TableCell>
                <TableCell align="right">{formatNumber(ad.total_leads)}</TableCell>
                <TableCell align="right">{formatRate(ad.open_rate)}</TableCell>
                <TableCell align="right">{formatRate(ad.click_rate)}</TableCell>
                <TableCell align="right">{formatRate(ad.lead_rate)}</TableCell>
                <TableCell align="right">{formatCurrency(ad.total_revenue)}</TableCell>
                <TableCell align="right">{formatCurrency(ad.etko)}</TableCell>
                <TableCell align="right">
                  {(ad.anfo_count || 0) === 0 && (ad.total_leads || 0) === 0
                    ? '/'
                    : formatNumber(ad.anfo_count)}
                </TableCell>
                <TableCell align="right">
                  {(ad.anfo_30d_revenue || 0) === 0 && (ad.total_leads || 0) === 0
                    ? '/'
                    : formatCurrency(ad.anfo_30d_revenue)}
                </TableCell>
                <TableCell align="right">
                  {(ad.total_revenue || 0) === 0 ? '/' : `${formatNumber(ad.roi_30)}%`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[50, 100, 200, 500]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Detail Modal */}
      {selectedAd && (
        <AdStatisticsDetailModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedAd(null);
          }}
          adId={selectedAd.ad_id}
        />
      )}

      {/* Hover Preview Popover */}
      <AdPreviewPopover
        open={hoveredAdId !== null}
        anchorPosition={anchorPosition}
        materialId={hoveredAdId}
        productName={
          hoveredAdId ? sortedData.find((ad) => ad.ad_id === hoveredAdId)?.product_name : undefined
        }
        onClose={handlePreviewClose}
      />
    </Box>
  );
};

export default AdStatisticsTable;
